'use client'

import { useLockScroll } from '@adamdotai/design-system'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import type { MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { LexicalTypeaheadMenuPlugin } from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { mergeRegister } from '@lexical/utils'
import { useClient } from '@urql/next'
import type { LexicalNode } from 'lexical'
import {
    $createRangeSelection,
    $getNodeByKeyOrThrow,
    $setSelection,
    COMMAND_PRIORITY_CRITICAL,
    COMMAND_PRIORITY_NORMAL,
    TextNode,
} from 'lexical'
import { uniqBy } from 'lodash'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as ReactDOM from 'react-dom'
import { useDebounceValue } from 'usehooks-ts'
import type { AdamContentItemNode } from '../../../nodes'
import { useEditorMetaData } from '../../../providers'
import { useUsersFilter } from '../../../services/useUsersFilter'
import type { EditorUser, QueryMatch } from '../../../types'
import type { AtMention } from '../../../types/at-mention'
import { formatUsersWithoutCreator } from '../../../utils/formatUsersWithoutCreator'
import { $isTableCellNode } from '../../Table'
import {
    ALIAS_LENGTH_LIMIT,
    MIN_MATCH_LENGTH,
    QUERY_LENGTH_LIMIT,
    SUGGESTION_LIST_LENGTH_LIMIT,
    TRIGGERS,
    VALID_CHARS,
    VALID_JOINS,
} from '../config'
import { insertTextAfterSelection } from '../insertTextAfterSelection'
import { MentionsFloatingBox } from '../MentionsFloatingBox'
import { AtMentionItemListWithHint } from './AtMentionItemListWithHint'
import { AtMentionItemListWithNoOptions } from './AtMentionItemListWithNoOptions'
import { AtMentionItemListWithOptions } from './AtMentionItemListWithOptions'
import type { MetionOption } from './AtMentionTypeaheadOption'
import {
    AllMeetingAttendeesMentionOption,
    IndividualAttendeeMentionOption,
} from './AtMentionTypeaheadOption'
import { INSERT_AT_MENTION } from './commands'
import { $createAtMentionNode, AtMentionNode } from './nodes/AtMentionNode'

const AT_SIGN_MAYBE_LEADING_SPACE_CAPTURE_GROUP_INDEX = 1
const AT_SIGN_TEXT_TO_REPLACE_CAPTURE_GROUP_INDEX = 2
const AT_SIGN_QUERY_CAPTURE_GROUP_INDEX = 3

const AtSignMentionsRegex = new RegExp(
    `(^|\\s|\\()(` +
        `[${TRIGGERS}]` +
        `((?:${VALID_CHARS}${VALID_JOINS}){0,${QUERY_LENGTH_LIMIT}})` +
        `)$`,
)

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
    `(^|\\s|\\()(` +
        `[${TRIGGERS}]` +
        `((?:${VALID_CHARS}){0,${ALIAS_LENGTH_LIMIT}})` +
        `)$`,
)

const mentionsCache = new Map<string, Array<EditorUser>>()

function useMentionLookupService(mentionString: string) {
    const { meetingId, users, currentUser } = useEditorMetaData()

    const [results, setResults] = useState<Array<EditorUser>>([])

    const [debouncedSearchString] = useDebounceValue(mentionString, 500)

    const { getData, data, fetching } = useUsersFilter(
        debouncedSearchString,
        'filter',
        useMemo(
            () => (currentUser?.id ? [currentUser?.id] : []),
            [currentUser?.id],
        ),
    )

    useEffect(() => {
        if (!meetingId) {
            getData()
        } else {
            const cachedResults = mentionsCache.get(debouncedSearchString)

            if (debouncedSearchString == null || debouncedSearchString === '') {
                setResults([])
                return
            }

            if (cachedResults === null) {
                return
            }
            if (cachedResults !== undefined) {
                setResults(cachedResults)
                return
            }

            const filteredUsers = users.filter(
                ({ name, email }) =>
                    name
                        .toLowerCase()
                        .includes(debouncedSearchString.toLowerCase()) ||
                    email
                        .toLowerCase()
                        .includes(debouncedSearchString.toLowerCase()),
            )
            mentionsCache.set(debouncedSearchString, filteredUsers)

            setResults(filteredUsers)
        }
    }, [debouncedSearchString, getData, meetingId, users])

    useEffect(() => {
        if (data?.users?.nodes && !fetching) {
            const options = formatUsersWithoutCreator(
                data.users?.nodes,
                currentUser?.id,
            )
            setResults(uniqBy([...options], (op) => op?.id))
        }
    }, [currentUser?.id, data, fetching])

    return results
}

function checkForAtSignMentions(
    text: string,
    minMatchLength: number,
): QueryMatch | null {
    let match = AtSignMentionsRegex.exec(text)

    if (match === null) {
        match = AtSignMentionsRegexAliasRegex.exec(text)
    }
    if (match !== null) {
        // The strategy ignores leading whitespace but we need to know it's
        // length to add it to the leadOffset
        const maybeLeadingWhitespace =
            match[AT_SIGN_MAYBE_LEADING_SPACE_CAPTURE_GROUP_INDEX]

        const matchingString = match[AT_SIGN_QUERY_CAPTURE_GROUP_INDEX]
        if (matchingString.length >= minMatchLength) {
            return {
                leadOffset: match.index + maybeLeadingWhitespace.length,
                matchingString,
                replaceableString:
                    match[AT_SIGN_TEXT_TO_REPLACE_CAPTURE_GROUP_INDEX],
            }
        }
    }
    return null
}

function getPossibleQueryMatch(text: string): QueryMatch | null {
    // 🔻 CHANGE(removed `checkForCapitalizedNameMentions`, feature not required)
    return checkForAtSignMentions(text, MIN_MATCH_LENGTH)
    // 🔺
}

function createMentionFromOption(option: MenuOption): AtMention {
    if (option instanceof IndividualAttendeeMentionOption) {
        return {
            type: 'person',
            attendeeName: option.name,
            userId: option.id,
            user: option.user,
        }
    }
    return {
        type: 'meeting',
    }
}

const MentionDropdownContent = ({
    queryString,
    options,
    selectOptionAndCleanUp,
    selectedIndex,
    setHighlightedIndex,
}: {
    queryString: string
    options: MetionOption[]
    selectOptionAndCleanUp: (option: MenuOption) => void
    selectedIndex: number | null
    setHighlightedIndex: (index: number) => void
}) => {
    if (queryString === '') {
        return <AtMentionItemListWithHint />
    }

    if (options.length > 0) {
        return (
            <AtMentionItemListWithOptions
                options={options}
                selectOptionAndCleanUp={selectOptionAndCleanUp}
                selectedIndex={selectedIndex}
                setHighlightedIndex={setHighlightedIndex}
            />
        )
    }

    return <AtMentionItemListWithNoOptions />
}

type AtMentionsPluginProps = {
    disableEmailsFlag?: boolean
    disableMentioningAllMeetingAttendees?: boolean
}

export function AtMentionsPlugin({
    disableEmailsFlag = false,
    disableMentioningAllMeetingAttendees = false,
}: AtMentionsPluginProps) {
    const { lockScroll, unLockScroll } = useLockScroll({
        targetId: 'content-space-area',
    })
    const [editor] = useLexicalComposerContext()
    const { users, meetingId } = useEditorMetaData()

    const urqlClient = useClient()

    const [queryString, setQueryString] = useState<string | null>('')

    const candidateUsers = useMentionLookupService(queryString ?? '')

    const options = useMemo(() => {
        const result: Array<MenuOption> = candidateUsers
            .map(
                (user, index) =>
                    new IndividualAttendeeMentionOption({
                        index,
                        id: user?.id,
                        email: user.email,
                        imgUrl: user.imageUrl,
                        name: user.name,
                        user,
                    }),
            )
            .slice(0, SUGGESTION_LIST_LENGTH_LIMIT)

        if (result.length == 0) {
            return []
        }

        if (!disableMentioningAllMeetingAttendees) {
            result.unshift(new AllMeetingAttendeesMentionOption())
        }

        return result
    }, [candidateUsers, disableMentioningAllMeetingAttendees])

    const onSelectOption = useCallback(
        (
            selectedOption: MenuOption,
            nodeToReplace: TextNode | null,
            closeMenu: () => void,
        ) => {
            editor.update(() => {
                const mentionNode = $createAtMentionNode(
                    createMentionFromOption(selectedOption),
                )
                if (nodeToReplace) {
                    nodeToReplace.replace(mentionNode)
                    const textNode = new TextNode(' ')
                    mentionNode.insertAfter(textNode)
                    textNode.selectEnd()
                }
                closeMenu()
            })
        },
        [editor],
    )

    React.useEffect(() => {
        if (disableEmailsFlag) {
            return mergeRegister(
                editor.registerCommand(
                    INSERT_AT_MENTION,
                    () => {
                        const textNodeWithAt = insertTextAfterSelection('@')
                        if (textNodeWithAt == null) return false
                        const newSelection = $createRangeSelection()
                        newSelection.anchor.set(
                            textNodeWithAt.getParentOrThrow().getKey(),
                            textNodeWithAt.getIndexWithinParent() + 1,
                            'element',
                        )
                        newSelection.focus.set(
                            textNodeWithAt.getParentOrThrow().getKey(),
                            textNodeWithAt.getIndexWithinParent() + 1,
                            'element',
                        )

                        $setSelection(newSelection)
                        return true
                    },
                    COMMAND_PRIORITY_CRITICAL,
                ),
            )
        }

        return mergeRegister(
            editor.registerCommand(
                INSERT_AT_MENTION,
                () => {
                    const textNodeWithAt = insertTextAfterSelection('@')
                    if (textNodeWithAt == null) return false
                    const newSelection = $createRangeSelection()
                    newSelection.anchor.set(
                        textNodeWithAt.getParentOrThrow().getKey(),
                        textNodeWithAt.getIndexWithinParent() + 1,
                        'element',
                    )
                    newSelection.focus.set(
                        textNodeWithAt.getParentOrThrow().getKey(),
                        textNodeWithAt.getIndexWithinParent() + 1,
                        'element',
                    )
                    $setSelection(newSelection)
                    return true
                },
                COMMAND_PRIORITY_CRITICAL,
            ),

            editor.registerMutationListener(
                AtMentionNode,
                async (mutatedNodes, { updateTags }) => {
                    if (
                        updateTags.has('skip-mutation-listener') ||
                        updateTags.has('reorder') ||
                        updateTags.has('collaboration') ||
                        updateTags.has('history-merge')
                    )
                        return

                    editor.getEditorState().read(async () => {
                        const createdNodes: LexicalNode[] = []

                        // classify nodes into array by mutation
                        mutatedNodes.forEach((mutation, nodeKey) => {
                            if (mutation === 'created') {
                                const node: AtMentionNode =
                                    $getNodeByKeyOrThrow(nodeKey)
                                createdNodes.push(node)
                            }
                        })

                        const requests: Promise<unknown>[] = []
                        if (createdNodes.length > 0) {
                            const createRequests = createdNodes.map(
                                (node: AtMentionNode) => {
                                    let topLevelElement: AdamContentItemNode =
                                        node.getTopLevelElement() as unknown as AdamContentItemNode
                                    const topLevelElementParent =
                                        topLevelElement.getParent()
                                    if (
                                        $isTableCellNode(topLevelElementParent)
                                    ) {
                                        // First parent is table row node, second parent is table node
                                        topLevelElement = topLevelElementParent
                                            .getParentOrThrow()
                                            .getParent() as unknown as AdamContentItemNode
                                    }
                                    const contentItemId =
                                        topLevelElement.getId()

                                    if (!contentItemId)
                                        return new Promise(() => {})

                                    return node.sendEmails(
                                        urqlClient,
                                        users,
                                        meetingId,
                                        topLevelElement
                                            .getContentType?.()
                                            .toUpperCase() ?? 'NOTE',
                                        topLevelElement.getId(),
                                        'RELATED',
                                        node
                                            .getTopLevelElementOrThrow()
                                            .getTextContent()
                                            .trim(),
                                        topLevelElement.getTextContent().trim(),
                                    )
                                },
                            )

                            requests.push(...createRequests)
                        }

                        await Promise.allSettled(requests)
                    })
                },
            ),
        )
    }, [disableEmailsFlag, editor, meetingId, urqlClient, users])

    return (
        <LexicalTypeaheadMenuPlugin<MenuOption>
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            triggerFn={getPossibleQueryMatch}
            options={options}
            onOpen={lockScroll}
            onClose={unLockScroll}
            commandPriority={COMMAND_PRIORITY_NORMAL}
            menuRenderFn={(
                anchorElementRef,
                { selectOptionAndCleanUp, setHighlightedIndex, selectedIndex },
            ) =>
                anchorElementRef.current &&
                ReactDOM.createPortal(
                    <MentionsFloatingBox anchor={anchorElementRef?.current}>
                        <MentionDropdownContent
                            queryString={queryString ?? ''}
                            options={options}
                            selectOptionAndCleanUp={selectOptionAndCleanUp}
                            selectedIndex={selectedIndex}
                            setHighlightedIndex={setHighlightedIndex}
                        />
                    </MentionsFloatingBox>,
                    anchorElementRef.current,
                )
            }
        />
    )
}
